import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  Input,
  TextArea,
} from '../../components/ui';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Add,
  Search,
  Edit,
  Delete,
  PersonAdd,
  Cancel,
  Warning,
  LocalOffer,
  MonetizationOn
} from '../../mui-icons';
import './InfluencersPage.css';

const InfluencersPage = () => {
  const { canEdit, canDelete } = useAdminAuth();
  const navigate = useNavigate();
  
  // States
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', document: '', phone: '', notes: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Create Coupon Confirmation Modal
  const [showCreateCouponModal, setShowCreateCouponModal] = useState(false);
  const [newInfluencer, setNewInfluencer] = useState(null);

  // Load influencers
  const loadInfluencers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(activeOnly && { active_only: true })
      };

      const response = await adminAPI.getInfluencers(params);
      
      if (response.status === 'success') {
        setInfluencers(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar influenciadores:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, activeOnly]);

  useEffect(() => {
    loadInfluencers();
  }, [loadInfluencers]);

  // Handle search with debounce
  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setFormData({ name: '', email: '', document: '', phone: '', notes: '' });
    setFormError('');
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (influencer) => {
    setModalMode('edit');
    setSelectedInfluencer(influencer);
    setFormData({
      name: influencer.name || '',
      email: influencer.email || '',
      document: influencer.document || '',
      phone: influencer.phone || '',
      notes: influencer.notes || ''
    });
    setFormError('');
    setShowModal(true);
  };

  // Open delete modal
  const openDeleteModal = (influencer) => {
    setSelectedInfluencer(influencer);
    setShowDeleteModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    // Validation
    if (!formData.name || formData.name.length < 2) {
      setFormError('Nome deve ter pelo menos 2 caracteres');
      setFormLoading(false);
      return;
    }

    try {
      if (modalMode === 'create') {
        const response = await adminAPI.createInfluencer(formData);
        setShowModal(false);
        loadInfluencers();
        
        // Mostrar modal perguntando se quer criar cupom
        if (response.status === 'success' && response.data) {
          setNewInfluencer(response.data);
          setShowCreateCouponModal(true);
        }
      } else {
        await adminAPI.updateInfluencer(selectedInfluencer.idinfluencer, formData);
        setShowModal(false);
        loadInfluencers();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };
  
  // Handle create coupon confirmation
  const handleCreateCoupon = () => {
    setShowCreateCouponModal(false);
    navigate('/admin/coupons', { 
      state: { 
        openCreateModal: true, 
        preselectedInfluencer: newInfluencer 
      } 
    });
  };
  
  const handleSkipCoupon = () => {
    setShowCreateCouponModal(false);
    setNewInfluencer(null);
  };

  // Handle delete
  const handleDelete = async (hardDelete = false) => {
    setDeleteLoading(true);

    try {
      await adminAPI.deleteInfluencer(selectedInfluencer.idinfluencer, hardDelete);
      setShowDeleteModal(false);
      loadInfluencers();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Toggle active status
  const toggleActive = async (influencer) => {
    try {
      await adminAPI.updateInfluencer(influencer.idinfluencer, { active: !influencer.active });
      loadInfluencers();
    } catch (err) {
      alert(err.message);
    }
  };

  // Render loading skeleton
  if (loading && influencers.length === 0) {
    return (
      <div className="influencers-page">
        <div className="page-header">
          <Skeleton width={200} height={32} />
          <Skeleton width={150} height={40} />
        </div>
        <div className="admin-card pn-card pn-card--default">
          <Skeleton width={300} height={40} style={{ marginBottom: 20 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} style={{ marginBottom: 12 }} />
          ))}
        </div>
      </div>
    );
  }

  // Sub-navigation items para o grupo Influenciadores
  const subNavItems = [
    { path: '/admin/influencers', label: 'Influenciadores', icon: <PersonAdd sx={{ fontSize: 18 }} /> },
    { path: '/admin/coupons', label: 'Cupons', icon: <LocalOffer sx={{ fontSize: 18 }} /> },
    { path: '/admin/commissions', label: 'Comissões', icon: <MonetizationOn sx={{ fontSize: 18 }} /> }
  ];

  return (
    <div className="influencers-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <PersonAdd sx={{ fontSize: 32 }} />
          <div>
            <h1>Influenciadores</h1>
            <p>{pagination.total} influenciador(es) cadastrado(s)</p>
          </div>
        </div>
        
        {canEdit() && (
          <Button variant="primary" onClick={openCreateModal}>
            <Add />
            Novo Influenciador
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
            <span>Apenas ativos</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadInfluencers}>
              Tentar novamente
            </Button>
          </div>
        ) : influencers.length === 0 ? (
          <div className="empty-state">
            <PersonAdd sx={{ fontSize: 64 }} className="empty-state-icon" />
            <h3 className="empty-state-title">Nenhum influenciador encontrado</h3>
            <p className="empty-state-text">
              {search ? 'Tente uma busca diferente' : 'Comece adicionando um novo influenciador'}
            </p>
            {canEdit() && !search && (
              <Button variant="primary" onClick={openCreateModal}>
                <Add />
                Adicionar Influenciador
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Cupons</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {influencers.map((influencer) => (
                    <tr key={influencer.idinfluencer}>
                      <td>
                        <div className="influencer-name-cell">
                          <strong>{influencer.name}</strong>
                          {influencer.document && (
                            <span className="document-text">{influencer.document}</span>
                          )}
                        </div>
                      </td>
                      <td>{influencer.email || '-'}</td>
                      <td>{influencer.phone || '-'}</td>
                      <td>
                        <span className="coupon-count">
                          {influencer.active_coupons || 0} / {influencer.total_coupons || 0}
                        </span>
                      </td>
                      <td>
                        <span 
                          className={`status-badge ${influencer.active ? 'status-active' : 'status-inactive'}`}
                          onClick={() => canEdit() && toggleActive(influencer)}
                          style={{ cursor: canEdit() ? 'pointer' : 'default' }}
                        >
                          {influencer.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {canEdit() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(influencer)}
                              title="Editar"
                              aria-label="Editar"
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </Button>
                          )}
                          {canEdit() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteModal(influencer)}
                              title="Excluir"
                              aria-label="Excluir"
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>

                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Novo Influenciador' : 'Editar Influenciador'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={formLoading}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" form="influencer-form" loading={formLoading}>
              {modalMode === 'create' ? 'Criar' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form id="influencer-form" onSubmit={handleSubmit} className="modal-form">
          <Input
            label="Nome *"
            placeholder="Nome do influenciador"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={formLoading}
            required
          />
          <Input
            label="Email"
            type="email"
            placeholder="email@exemplo.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            disabled={formLoading}
          />
          <div className="form-row">
            <Input
              label="CPF/CNPJ"
              placeholder="Documento"
              value={formData.document}
              onChange={(e) => setFormData({ ...formData, document: e.target.value })}
              disabled={formLoading}
            />
            <Input
              label="Telefone"
              type="tel"
              placeholder="+55 11 99999-9999"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={formLoading}
            />
          </div>
          <TextArea
            label="Observações"
            placeholder="Observações sobre o influenciador..."
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            disabled={formLoading}
            rows={3}
          />
          {formError && (
            <div className="form-error">
              <Warning />
              <span>{formError}</span>
            </div>
          )}
        </form>
      </FormModal>

      <FormModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir Influenciador"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => handleDelete(false)} loading={deleteLoading}>
              Desativar
            </Button>
            {canDelete() && (
              <Button
                variant="danger"
                onClick={() => handleDelete(true)}
                loading={deleteLoading}
                style={{ background: '#7B1FA2' }}
              >
                Excluir Permanente
              </Button>
            )}
          </>
        }
      >
        <p>
          Tem certeza que deseja excluir o influenciador <strong>{selectedInfluencer?.name}</strong>?
        </p>
        {selectedInfluencer?.total_coupons > 0 && (
          <p>
            Este influenciador possui <strong>{selectedInfluencer.total_coupons} cupom(ns)</strong>.
          </p>
        )}
      </FormModal>

      <FormModal
        open={showCreateCouponModal}
        onClose={handleSkipCoupon}
        title="Influenciador Criado!"
        footer={
          <>
            <Button variant="secondary" onClick={handleSkipCoupon}>
              Não, obrigado
            </Button>
            <Button variant="primary" onClick={handleCreateCoupon}>
              Sim, criar cupom
            </Button>
          </>
        }
      >
        <p>
          O influenciador <strong>{newInfluencer?.name}</strong> foi criado com sucesso!
        </p>
        <p>Deseja criar um cupom para este influenciador agora?</p>
      </FormModal>
    </div>
  );
};

export default InfluencersPage;

